import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Checkbox, Input, Button } from "semantic-ui-react";
import { useGridFilterDisplay } from "ag-grid-react";


const CustomSetFilter = ({ model, onModelChange, api, getValue, doesRowPassOtherFilter }) => {

  const [selectedValues, setSelectedValues] = useState(new Set());
  const [allValues, setAllValues] = useState([]);
  const [availableValues, setAvailableValues] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [initialized, setInitialized] = useState(false);

  const refreshAvailableValues = useCallback(() => {
    if (!api || !getValue || !doesRowPassOtherFilter) return [];

    const values = new Set();
    api.forEachNode((node) => {
      if (doesRowPassOtherFilter(node)) {
        const value = getValue(node);
        if (value != null) {
          values.add(value);
        }
      }
    });

    return Array.from(values).sort((a, b) => {
      if (a == null) return 1;
      if (b == null) return -1;
      return String(a).localeCompare(String(b));
    });
  }, [api, getValue, doesRowPassOtherFilter]);

  const onAnyFilterChanged = useCallback(() => {
    const sortedValues = refreshAvailableValues();
    setAvailableValues(sortedValues);
  }, [refreshAvailableValues]);

  useGridFilterDisplay({
    onAnyFilterChanged,
  });

  useEffect(() => {
    if (!api || !getValue || !doesRowPassOtherFilter) return;

    const sortedValues = refreshAvailableValues();
    setAllValues(sortedValues);
    setAvailableValues(sortedValues);

    if (model && model.values) {
      setSelectedValues(new Set(model.values));
    } else {
      setSelectedValues(new Set(sortedValues));
    }
    setInitialized(true);
  }, [api, getValue, doesRowPassOtherFilter, refreshAvailableValues]);


  const filteredValues = useMemo(() =>
    availableValues.filter((value) =>
      String(value).toLowerCase().includes(searchText.toLowerCase())
    ),
    [availableValues, searchText]
  );

  const selectAll = useMemo(() => {
    if (filteredValues.length === 0) return false;
    return filteredValues.every((v) => selectedValues.has(v));
  }, [filteredValues, selectedValues]);

  const someSelected = useMemo(() =>
    !selectAll && filteredValues.some((v) => selectedValues.has(v)),
    [selectAll, filteredValues, selectedValues]
  );

  const handleCheckboxChange = useCallback((value, checked) => {
    setSelectedValues((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(value);
      } else {
        next.delete(value);
      }
      return next;
    });
  }, []);

  const handleSelectAllChange = useCallback((checked) => {
    setSelectedValues((prev) => {
      const next = new Set(prev);
      if (checked) {
        filteredValues.forEach((v) => next.add(v));
      } else {
        filteredValues.forEach((v) => next.delete(v));
      }
      return next;
    });
  }, [filteredValues]);

  const handleApply = useCallback(() => {
    // Check if all available values are selected (means no filter needed)
    const allAvailableSelected = availableValues.every((v) => selectedValues.has(v));
    const newModel = allAvailableSelected ? null : { values: Array.from(selectedValues) };
    onModelChange(newModel);
  }, [onModelChange, selectedValues, availableValues]);

  const handleReset = useCallback(() => {
    setSelectedValues(new Set(allValues));
    setSearchText("");
    onModelChange(null);
  }, [onModelChange, allValues]);

  if (!initialized) {
    return <div className="custom-set-filter">Loading...</div>;
  }

  return (
    <div className="custom-set-filter">
      <div className="custom-set-filter-search">
        <Input
          size="mini"
          icon="search"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          fluid
        />
      </div>

      <div className="custom-set-filter-select-all">
        <Checkbox
          label="(Select All)"
          checked={selectAll}
          indeterminate={someSelected}
          onChange={(e, data) => handleSelectAllChange(data.checked)}
        />
      </div>

      <div className="custom-set-filter-values">
        {filteredValues.map((value) => (
          <div key={String(value)} className="custom-set-filter-value">
            <Checkbox
              label={String(value) || "(Blank)"}
              checked={selectedValues.has(value)}
              onChange={(e, data) => handleCheckboxChange(value, data.checked)}
            />
          </div>
        ))}
        {filteredValues.length === 0 && (
          <div className="custom-set-filter-empty">No matches</div>
        )}
      </div>

      <div className="custom-set-filter-buttons">
        <Button size="mini" onClick={handleReset}>
          Reset
        </Button>
        <Button size="mini" primary onClick={handleApply}>
          Apply
        </Button>
      </div>
    </div>
  );
};

/**
 * doesFilterPass callback for the custom set filter.
 *
 * params contains: { model, handlerParams, node, data }
 * - model: the current filter model
 * - data: The data part of the row node in question.
 * - node: The row node in question.
 * - handlerParams: Utility params that would be passed to the handler, including getValue which provides access to the cell values.
 */
export const customSetFilterDoesFilterPass = ({model, node, handlerParams}) => {
  const value = handlerParams.getValue(node);
  const selectedValues = new Set(model.values);

  return selectedValues.has(value);
};

export default CustomSetFilter;
